import { AuthNavParams } from '@euler/app/flows/auth/Auth';
import { useAuthTheme } from '@euler/app/flows/auth/hooks';
import { useColorMode } from '@euler/app/theming/color-mode';
import { kDefaultPresentationColorMode } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily, Heading } from '@euler/components/typography';
import { useQrcodeAuth } from '@euler/functions/auth/useQrcodeAuth';
import { useIsHub } from '@euler/functions/isHub';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { FC, memo } from 'react';
import {
  Platform,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
} from 'react-native';

export const LoginHeader: FC<{
  title?: string;
  subTitle?: string;
  subTitleStyle?: StyleProp<TextStyle>;
  avatar?: string;
  qrcodeButton?: boolean;
}> = memo(props => {
  const isMobile = useIsMobileLayout();
  const theme = useAuthTheme();
  const { rpx } = useResponsiveContext();
  const navigation = useNavigation<StackNavigationProp<AuthNavParams>>();
  const { qrcodeChallenge, scanQrcode } = useQrcodeAuth({
    onAppSelection: ({ apps, authToken }) => {
      navigation.push('_AppSelection', { apps, authToken });
    },
  });
  const isHub = useIsHub();
  const colorMode = useColorMode(
    isHub ? kDefaultPresentationColorMode : undefined,
  );

  return (
    <HStack
      css={`
        margin-bottom: ${rpx(32)}px;
        padding-right: ${rpx(8)}px;
      `}
    >
      <View
        css={`
          align-items: ${isMobile ? 'flex-start' : 'center'};
          flex: 1;
        `}
      >
        <Heading
          text={props.title ?? '欢迎您'}
          css={`
            color: ${theme.loginHeaderTitleTextColor};
            font-family: ${FontFamily.NotoSans.Light};
            font-size: ${rpx(32)}px;
            margin-bottom: ${rpx(8)}px;
          `}
        />
        {props.subTitle ? (
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(16)}px;
              color: ${theme.loginHeaderSubTitleTextColor};
            `}
            style={props.subTitleStyle}
          >
            {props.subTitle}
          </Text>
        ) : null}
      </View>
      {props.qrcodeButton === true && (
        <TouchableOpacity
          hitSlop={PredefinedHitSlops.large}
          onPress={
            Platform.OS === 'web' || isHub ? qrcodeChallenge : scanQrcode
          }
        >
          <Icon
            name={Platform.OS === 'web' || isHub ? 'qrcode' : 'scan'}
            size={32}
            color={
              colorMode === 'dark'
                ? 'rgba(255, 255, 255, 0.5)'
                : 'rgba(0, 0, 0, 0.5)'
            }
          />
        </TouchableOpacity>
      )}
    </HStack>
  );
});
